// In-app manual content access. Every `content/*.md` file is eager-raw-imported
// at build time via Vite's glob, so adding a topic file needs no code change here
// (only a manifest entry to place it in the table of contents).
//
// Each topic file follows the convention in docs/user-manual.md: a single `# H1`
// title, a `_Last updated: ..._` stamp, then a `## What this page covers` intro.
// Title and summary are DERIVED from the file so they are single-sourced - the
// manifest only owns ordering + grouping.

const modules = import.meta.glob('./content/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

const bodyBySlug: Record<string, string> = {};
for (const [path, content] of Object.entries(modules)) {
  const slug = path.replace(/^.*\/([^/]+)\.md$/, '$1');
  bodyBySlug[slug] = content;
}

export function helpTopicBody(slug: string): string | undefined {
  return bodyBySlug[slug];
}

export function helpTopicExists(slug: string): boolean {
  return slug in bodyBySlug;
}

// The file's first `# H1`, or the slug as a fallback.
export function helpTopicTitle(slug: string): string {
  const body = bodyBySlug[slug];
  if (!body) return slug;
  const m = body.match(/^#\s+(.+)$/m);
  return m ? m[1].trim() : slug;
}

// The first paragraph under "## What this page covers" - the TOC blurb. Empty
// string if the section is absent.
export function helpTopicSummary(slug: string): string {
  const body = bodyBySlug[slug];
  if (!body) return '';
  const lines = body.split('\n');
  const start = lines.findIndex((l) =>
    /^##\s+what this page covers/i.test(l.trim())
  );
  if (start === -1) return '';
  const para: string[] = [];
  for (const line of lines.slice(start + 1)) {
    const t = line.trim();
    if (t.startsWith('#')) break; // next heading
    if (t === '') {
      if (para.length) break;
      else continue;
    }
    para.push(t);
  }
  return para.join(' ');
}

export function allHelpSlugs(): string[] {
  return Object.keys(bodyBySlug);
}
